import dayjs from "dayjs";
import { compact, isArray, isEmpty } from "lodash";
import relativeTime from "dayjs/plugin/relativeTime";
import { fileToBase64 } from "utils/helper";

export const passwordRegExp =
    /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*?])[a-zA-Z0-9!@#$%^&*?]{9,16}$/;

export const setDataToObj = (obj: Record<string, any>, key: string, value: any = ""): Record<string, any> | false => {
    if (!obj || !key || typeof obj !== "object") return false;
    const keyAtt = key.split(".");
    if (keyAtt.length) {
        if (keyAtt.length === 1) {
            obj[keyAtt[0]] = value;
        } else {
            obj[keyAtt[0]] = setDataToObj(
                obj[keyAtt[0]] ?? {},
                keyAtt.slice(1).join("."),
                value
            );
        }
    }
    return { ...obj };
};

export const getDataFromObject = (data: Record<string, any>, key: string): any | false => {
    if (!data || !key || typeof data !== "object") return false;
    key = key.replace(/\[(\w+)\]/g, ".$1").replace(/^\./, "");
    let keys = key.split(".");

    for (let i = 0, n = keys.length; i < n; ++i) {
        const _key = keys[i];
        if (_key in data && data[_key]) {
            data = data[_key];
        } else {
            return false;
        }
    }
    return data;
};

export const debounce = (func: (...args: any[]) => void, delay: number = 500) => {
    let timer: NodeJS.Timeout;
    return function (...args: any[]) {
        const context = this;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            func.apply(context, args);
        }, delay);
    };
};

export const cleanObj = (object: Record<string, any>): Record<string, any> => {
    Object.entries(object).forEach(([key, value]) => {
        if (value === "" || value === null) {
            delete object[key];
        }
    });
    return object;
};

export const cleanDeepObj = (object: Record<string, any>): Record<string, any> => {
    if (typeof object === "object" && object !== null) {
        Object.entries(object).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) {
                delete object[key];
            }

            if (typeof value === "object" && value !== null) {
                const temp = cleanDeepObj(value);
                if (isEmpty(temp)) {
                    delete object[key];
                } else {
                    object[key] = isArray(temp) ? compact(temp) : temp;
                }
            }
        });
    }
    return object;
};

export const makeFormDataPayload = (
    payload: Record<string, any> = {},
    formData: FormData = new FormData(),
    prefix: string = ""
): FormData | false => {
    if (payload !== undefined) {
        if (typeof payload !== "object" && !prefix) return false;

        if (
            typeof payload === "object" &&
            !Array.isArray(payload) &&
            !(payload instanceof File)
        ) {
            Object.entries(payload).forEach(([key, value]) => {
                const keyWithPrefix = prefix ? `${prefix}.${key}` : key;

                if (value === null) {
                    formData.append(keyWithPrefix, "");
                } else {
                    makeFormDataPayload(value, formData, keyWithPrefix);
                }
            });
        } else if (Array.isArray(payload)) {
            if (payload.length === 0) {
                formData.append(prefix, "");
            } else {
                payload.forEach((val) => {
                    makeFormDataPayload(val, formData, prefix);
                });
            }
        } else {
            formData.append(prefix, payload);
        }
    }
    return formData;
};

export const getUpdatedFields = (currentValues: Record<string, any>, defaultValues: Record<string, any>): Record<string, any> => {
    const updatedFields: Record<string, any> = {};

    const arraysEqual = (arr1: any[], arr2: any[]) => JSON.stringify(arr1) === JSON.stringify(arr2);

    const compareObjects = (obj1: Record<string, any>, obj2: Record<string, any>): boolean => {
        if (!obj2) return false;
        for (let key in obj1) {
            if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
                if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
                    if (!compareObjects(obj1[key], obj2[key]))
                        return false;
                }
                else if (obj1[key] !== obj2[key])
                    return false;
            }
        }
        return true;
    };

    const compareArraysByValue = (arr1: any[], arr2: { value: any }[]) => {
        const arr2Values = arr2.map(item => item.value);
        return arraysEqual(arr1, arr2Values);
    };

    const compareValues = (currentValue: any, defaultValue: any): boolean => {
        if (currentValue === defaultValue) return false;
        const bothEmptyStrings = currentValue === '' && defaultValue === '';

        if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
            if (typeof defaultValue?.[0] === 'object')
                return !compareArraysByValue(currentValue, defaultValue);
            if (!arraysEqual(currentValue, defaultValue))
                return true;
        } else if (typeof currentValue === 'object' && typeof defaultValue === 'object') {
            if (!compareObjects(currentValue, defaultValue))
                return true;
        } else if (typeof defaultValue === 'object' && [defaultValue?.id, defaultValue?.value].includes(currentValue)) {
            return false;
        } else if (!currentValue && !defaultValue) {
            return false;
        } else if (bothEmptyStrings) {
            return false;
        }
        if (!Array.isArray(currentValue) && !Array.isArray(defaultValue) && currentValue !== defaultValue) {
            return true;
        }
        return false;
    };

    const hasArrayIndex = (path: string) => path.split('.').some(part => !isNaN(Number(part)));

    const traverseObject = (currentObj: Record<string, any>, defaultObj: Record<string, any>, path: string = '') => {
        if (!defaultObj) return;
        for (let key in currentObj) {
            if (currentObj.hasOwnProperty(key)) {
                const currentPath = path ? `${path}.${key}` : key;
                const currentValue = currentObj[key];
                const defaultValue = defaultObj[key];
                if (hasArrayIndex(currentPath))
                    continue;

                if (compareValues(currentValue, defaultValue))
                    updatedFields[currentPath] = currentValue;

                if (typeof currentValue === 'object' && typeof defaultValue === 'object')
                    traverseObject(currentValue, defaultValue, currentPath);
            }
        }
    };
    traverseObject(currentValues, defaultValues);

    return updatedFields;
};

export const processFilterFields = ({
    rest,
    schema = [],
    rangeFilters = [],
    dateFilters = [],
}: {
    rest: Record<string, any>;
    schema?: Array<{ type: string; children: Array<{ name: string; fieldType: string; fieldProps?: Record<string, any> }> }>;
    rangeFilters?: string[];
    dateFilters?: string[];
}) => {
    const searchFilter: Record<string, any> = {};
    const queryFilter: Record<string, any> = {};
    const range: Record<string, any> = {};

    [...rangeFilters, ...dateFilters].forEach((field) => {
        const fieldData = rest[field];
        delete rest[field];
        if (fieldData && (fieldData["0"] || fieldData["1"]))
            range[field] = [fieldData["0"], fieldData["1"]];
    });

    schema.forEach((item) => {
        if (item.type === "row") {
            item.children.forEach((child) => {
                if (!rest.hasOwnProperty(child.name)) return;
                const { name, fieldType, fieldProps } = child;
                if ((fieldType === "select" && fieldProps && !("mode" in fieldProps)) ||
                    (fieldType === "input"))
                    searchFilter[name] = rest[name];
                else queryFilter[name] = rest[name];
            });
        }
    });
    return { searchFilter, queryFilter, range };
};

export const transformPermissionsArray = (permissionsArray: Array<{ permissionId: { slug: string }; isAllowed: boolean }>): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    permissionsArray?.forEach(permissionObj => {
        result[permissionObj.permissionId.slug] = permissionObj.isAllowed;
    });
    return result;
};

export const getValueByKey = (object: Record<string, any>, keyPath: string): any => {
    return keyPath.split('.').reduce((acc, key) => acc && acc[key], object);
};

export const getDaysDiff = (creationDate: string | Date): string => {
    dayjs.extend(relativeTime);
    return dayjs().to(dayjs(creationDate));
};

export const getFileBase64Object = async (attachment: File | null): Promise<{ fileName: string; fileType: string; document: string } | {}> => {
    let attachmentDetails = {};
    if (attachment && typeof attachment === 'object') {
        const base64String = await fileToBase64(attachment);
        const [, modifiedBase64] = String(base64String).split("base64,");
        attachmentDetails = {
            fileName: attachment.name,
            fileType: attachment.type,
            document: modifiedBase64
        };
    }
    return attachmentDetails;
};

export const updateFile = (data: Record<string, any>, fileArr: string[] = []): Record<string, any> => {
    for (const fileType of fileArr) {
        if (data[fileType] && typeof data[fileType] === 'object') {
            data[fileType] = Array.isArray(data[fileType])
                ? data[fileType].map((i: any) => i?.document)
                : data[fileType]?.document;
        }
    }
    return data;
};
